import React, { useState, useEffect } from 'react';
import { AlertTriangle, Info, XCircle } from 'lucide-react';

type SsoBannerSeverity = 'info' | 'warning' | 'error';

interface SsoStatusDetail {
  reason?: string;
  reasonCode?: string;
  severity?: SsoBannerSeverity;
  expected?: boolean;
}

interface BannerState {
  reason: string;
  severity: SsoBannerSeverity;
}

const severityConfig: Record<
  SsoBannerSeverity,
  {
    title: string;
    containerClassName: string;
    iconClassName: string;
    Icon: React.ComponentType<{ className?: string }>;
  }
> = {
  info: {
    title: 'Aguardando o Launcher',
    containerClassName: 'border-blue-500/25 bg-blue-500/12 text-blue-50',
    iconClassName: 'text-blue-300',
    Icon: Info
  },
  warning: {
    title: 'Conexao com o Launcher instavel',
    containerClassName: 'border-amber-500/25 bg-amber-500/12 text-amber-50',
    iconClassName: 'text-amber-300',
    Icon: AlertTriangle
  },
  error: {
    title: 'Falha na autenticacao do painel',
    containerClassName: 'border-red-500/25 bg-red-500/12 text-red-50',
    iconClassName: 'text-red-300',
    Icon: XCircle
  }
};

/**
 * Banner exibido quando o SSO auto-login falha durante a inicialização.
 * Escuta o evento customizado 'sso:init-failed' e mostra uma mensagem
 * com opção de fechar. Segue padrão do ConnectivityBanner.
 */
export default function SsoStatusBanner() {
  const [banner, setBanner] = useState<BannerState | null>(null);

  useEffect(() => {
    const handleSsoFailed = (e: CustomEvent<SsoStatusDetail>) => {
      if (e.detail?.expected) {
        setBanner(null);
        return;
      }

      setBanner({
        reason: e.detail?.reason || 'Nao foi possivel concluir a autenticacao automatica.',
        severity: e.detail?.severity || 'warning'
      });
    };

    const clearBanner = () => setBanner(null);

    window.addEventListener('sso:init-failed', handleSsoFailed as EventListener);
    window.addEventListener('sso:auth-updated', clearBanner as EventListener);
    return () => {
      window.removeEventListener('sso:init-failed', handleSsoFailed as EventListener);
      window.removeEventListener('sso:auth-updated', clearBanner as EventListener);
    };
  }, []);

  if (!banner) return null;

  const config = severityConfig[banner.severity];
  const Icon = config.Icon;

  return (
    <div className="fixed left-1/2 top-4 z-[85] w-[min(92vw,42rem)] -translate-x-1/2 px-2">
      <div className={`flex items-start gap-3 rounded-2xl border px-4 py-3 shadow-2xl backdrop-blur ${config.containerClassName}`}>
        <div className={`mt-0.5 rounded-full p-1.5 bg-black/10 ${config.iconClassName}`}>
          <Icon className="h-4 w-4" />
        </div>

        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold">{config.title}</p>
          <p className="mt-1 text-sm leading-5 text-current/90">{banner.reason}</p>
        </div>

        <button
          className="rounded-md px-2 py-1 text-xs font-medium text-current/80 transition hover:bg-black/10 hover:text-current"
          onClick={() => setBanner(null)}
        >
          Fechar
        </button>
      </div>
    </div>
  );
}
